import { Edit, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import type { ImpactAnalysisData } from "@/types/impact-analysis";

const ARTICLE_QUESTIONS = {
  "9a": "Entitatea este singurul furnizor al unui serviciu care este esențial pentru susținerea unor activități societale și economice critice.",
  "9b": "Perturbarea serviciului furnizat de entitate ar putea avea un impact semnificativ asupra siguranței publice, a securității publice sau a sănătății publice.",
  "9c": "Perturbarea serviciului furnizat de entitate ar putea genera un risc sistemic transsectorial, cu un impact special pentru sectoarele în care o astfel de perturbare ar avea loc",
  "9d": "Entitatea este critică datorită importanței sale specifice la nivel național sau regional pentru sectorul sau tipul de servicii în cauză sau pentru alte sectoare",
} as const;

type QuestionCellProps = {
  questionText: string;
};

function QuestionCell({ questionText }: QuestionCellProps) {
  return (
    <TableCell className="p-1 text-sm border-r-2 border-black align-top bg-blue-200 text-black font-bold">
      <span className="hidden md:inline">{questionText}</span>
      <div className="flex items-center gap-1 md:hidden max-w-[150px] md:max-w-none">
        <span className="truncate flex-1">{questionText}</span>
        <TouchTooltip
          content={<p className="text-xs max-w-[200px]">{questionText}</p>}
        >
          <Info className="h-4 w-4 flex-shrink-0 cursor-pointer" />
        </TouchTooltip>
      </div>
    </TableCell>
  );
}

type ResultCellProps = {
  value: string;
  className: string;
};

function ResultCell({ value, className }: ResultCellProps) {
  return (
    <TableCell className={className}>
      <span className="hidden md:inline">{value}</span>
      <div className="flex items-center gap-1 justify-center md:hidden">
        <span className="truncate flex-1 text-center">{value}</span>
        {value.length > 10 && (
          <TouchTooltip
            content={<p className="text-xs max-w-[200px]">{value}</p>}
          >
            <Info className="h-3 w-3 flex-shrink-0 cursor-pointer" />
          </TouchTooltip>
        )}
      </div>
    </TableCell>
  );
}

type ImpactResultsTableProps = {
  impactData: ImpactAnalysisData;
  showEditButton?: boolean;
};

export function ImpactResultsTable({
  impactData,
  showEditButton = false,
}: ImpactResultsTableProps) {
  const navigate = useNavigate();
  const result = impactData.result ?? {
    article9a: null,
    article9b: null,
    article9c: null,
    article9d: null,
  };

  const nineA = result.article9a;
  const nineB = result.article9b;
  const nineC = result.article9c;
  const nineD = result.article9d;

  const format9a9d = (value: boolean | null): string => {
    if (value === true) return "Da";
    if (value === false) return "Nu";
    return "Nu ați selectat un răspuns";
  };

  const format9b9c = (value: "SCĂZUT" | "MEDIU" | "RIDICAT" | null): string => {
    if (value === "RIDICAT") return "Ridicat";
    if (value === "MEDIU") return "Mediu";
    if (value === "SCĂZUT") return "Scăzut";
    return "Nu ați răspuns/completat câmpuri relevante";
  };

  const getCellClassName = (value: string): string => {
    const baseClasses =
      "text-sm font-semibold text-center max-w-[100px] md:max-w-[190px] p-1";
    if (value === "Da" || value === "Ridicat") {
      return `${baseClasses} bg-red-600 text-black`;
    }
    if (value === "Mediu") {
      return `${baseClasses} bg-orange-500 text-black`;
    }
    if (value === "Nu" || value === "Scăzut") {
      return `${baseClasses} bg-yellow-300 text-black`;
    }
    return `${baseClasses} bg-gray-100 text-black`;
  };

  const nineADisplay = format9a9d(nineA);
  const nineBDisplay = format9b9c(nineB);
  const nineCDisplay = format9b9c(nineC);
  const nineDDisplay = format9a9d(nineD);

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Info className="h-5 w-5" />
          Rezultat final
          {showEditButton && (
            <Edit
              className="h-4 w-4 cursor-pointer hover:text-primary transition-colors ml-auto"
              onClick={() => navigate("/analiza-efect-perturbator")}
            />
          )}
        </CardTitle>
        <CardDescription className="text-xs md:text-sm">
          Sinteza evaluării conform Art. 9 din OUG 155/2024
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 md:pt-0">
        <div className="overflow-x-auto">
          <Table className="border-2 border-black">
            <TableBody>
              <TableRow className="border-2 border-black">
                <TableCell className="p-1 font-semibold text-sm border-r-2 border-black whitespace-nowrap align-top bg-blue-400 text-black">
                  Art. 9 <br /> lit. a)
                </TableCell>
                <QuestionCell questionText={ARTICLE_QUESTIONS["9a"]} />
                <ResultCell
                  value={nineADisplay}
                  className={getCellClassName(nineADisplay)}
                />
              </TableRow>

              <TableRow className="border-2 border-black">
                <TableCell className="p-1 font-semibold text-sm border-r-2 border-black whitespace-nowrap align-top bg-blue-400 text-black">
                  Art. 9 <br /> lit. b)
                </TableCell>
                <QuestionCell questionText={ARTICLE_QUESTIONS["9b"]} />
                <ResultCell
                  value={nineBDisplay}
                  className={getCellClassName(nineBDisplay)}
                />
              </TableRow>

              <TableRow className="border-2 border-black">
                <TableCell className="p-1 font-semibold text-sm border-r-2 border-black whitespace-nowrap align-top bg-blue-400 text-black">
                  Art. 9 <br /> lit. c)
                </TableCell>
                <QuestionCell questionText={ARTICLE_QUESTIONS["9c"]} />
                <ResultCell
                  value={nineCDisplay}
                  className={getCellClassName(nineCDisplay)}
                />
              </TableRow>

              <TableRow className="border-2 border-black">
                <TableCell className="p-1 font-semibold text-sm border-r-2 border-black whitespace-nowrap align-top bg-blue-400 text-black">
                  Art. 9 <br /> lit. d)
                </TableCell>
                <QuestionCell questionText={ARTICLE_QUESTIONS["9d"]} />
                <ResultCell
                  value={nineDDisplay}
                  className={getCellClassName(nineDDisplay)}
                />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
